/**
 * Hook para grabación de voz
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { AudioRecorderHelper } from '../utils/audioHelpers';
import { loadPolyfills } from '../utils/polyfills';

// Tope duro para evitar saturar Gemini STT (~20MB inline) y la memoria del
// browser. 12 min en webm 128kbps ≈ 11.5MB raw. Cubre exámenes B2/C1/C2 que
// suelen pedir monólogos de 8-10 min con margen.
export const MAX_RECORDING_SECONDS = 720;

// Duración mínima de un turno válido. Por debajo de esto suele ser un toque
// accidental del micrófono (abrir/cerrar sin hablar).
const MIN_RECORDING_SECONDS = 0.4;

/**
 * VAD por energía, simple y CONSERVADOR: decide si una grabación es
 * esencialmente silencio/ruido (sin voz). Evita que Galia responda cuando el
 * micrófono se abre en silencio, porque el STT (Whisper) tiende a "alucinar"
 * texto a partir de ruido. Diseñado para NO descartar habla real: el umbral de
 * fracción con voz es muy bajo (2%), muy por debajo de cualquier turno hablado.
 * Si la decodificación falla, devuelve isSilent=false (fail-open: mejor enviar
 * y dejar que el backend decida, que descartar voz real).
 */
async function analyzeSilence(blob: Blob): Promise<{ isSilent: boolean; durationSec: number }> {
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return { isSilent: false, durationSec: Infinity };
    const ctx = new AC();
    const arrayBuf = await blob.arrayBuffer();
    const audioBuf = await ctx.decodeAudioData(arrayBuf.slice(0));
    void ctx.close();

    const data = audioBuf.getChannelData(0);
    const durationSec = audioBuf.duration;
    const frameSize = Math.max(1, Math.floor(audioBuf.sampleRate * 0.02)); // ~20ms
    const VOICED_RMS = 0.01; // umbral de energía por frame para considerarlo "con voz"
    let voiced = 0;
    let frames = 0;
    for (let i = 0; i < data.length; i += frameSize) {
      const end = Math.min(i + frameSize, data.length);
      let sum = 0;
      for (let j = i; j < end; j++) sum += data[j] * data[j];
      const rms = Math.sqrt(sum / (end - i));
      frames++;
      if (rms > VOICED_RMS) voiced++;
    }
    const voicedRatio = frames > 0 ? voiced / frames : 0;
    return { isSilent: voicedRatio < 0.02, durationSec };
  } catch {
    return { isSilent: false, durationSec: Infinity };
  }
}

export interface UseVoiceRecorderReturn {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  recordingSeconds: number;
  maxRecordingSeconds: number;
  error: string | null;
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<AudioRecorderHelper | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);

  const stopTicker = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      await loadPolyfills();

      if (!recorderRef.current) {
        recorderRef.current = new AudioRecorderHelper();
      }

      await recorderRef.current.startRecording();
      startedAtRef.current = Date.now();
      setRecordingSeconds(0);
      setIsRecording(true);

      stopTicker();
      tickRef.current = setInterval(() => {
        setRecordingSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
    } catch (err: any) {
      setError(err.message || 'Error al iniciar la grabación');
      setIsRecording(false);
      console.error('Error al iniciar grabación:', err);
    }
  }, [stopTicker]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    try {
      stopTicker();
      if (!recorderRef.current || !isRecording) {
        return null;
      }

      const audioBlob = await recorderRef.current.stopRecording();
      setIsRecording(false);

      // Descartar silencio/ruido: no enviar al STT/LLM si no hubo voz real.
      if (audioBlob) {
        const { isSilent, durationSec } = await analyzeSilence(audioBlob);
        if (durationSec < MIN_RECORDING_SECONDS || isSilent) {
          console.info('[VoiceRecorder] Grabación descartada (silencio/ruido).', {
            durationSec: Number(durationSec.toFixed(2)),
            isSilent,
          });
          return null;
        }
      }
      return audioBlob;
    } catch (err: any) {
      setError(err.message || 'Error al detener la grabación');
      setIsRecording(false);
      console.error('Error al detener grabación:', err);
      return null;
    }
  }, [isRecording, stopTicker]);

  useEffect(() => () => stopTicker(), [stopTicker]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    recordingSeconds,
    maxRecordingSeconds: MAX_RECORDING_SECONDS,
    error,
  };
}


